"use client";

/**
 * Rótulo de desfecho: "Salvar → Salvando… → Salvo ✓" (e "Enviando → Enviado",
 * "Copiar → Copiado", "Publicar → No ar"). Transição de ESTADO, no mesmo lugar.
 *
 * As quatro faces moram empilhadas na mesma célula de grade: o rótulo tem a
 * largura da maior e nada ao redor pula (a mesma régua do `onAction` do
 * `Button`, 11/09). Só a face do estado atual aparece; a que sai sobe e
 * desfoca, a que entra pousa.
 */
import { m } from "motion/react";
import type { CSSProperties, ReactNode } from "react";
import { MarcaDeOk } from "./marca-de-ok";
import { useMovimento } from "./provider";

export type EstadoDaAcao = "parado" | "fazendo" | "feito" | "erro";

export function RotuloDeEstado({
  estado,
  rotulos,
  icones,
  alinhar = "inicio",
  className,
  style,
}: {
  estado: EstadoDaAcao;
  rotulos: { parado: ReactNode; fazendo: ReactNode; feito: ReactNode; erro?: ReactNode };
  /** Ícone de cada face. Padrão: nada no parado, girador no fazendo, ✓ que se desenha no feito. */
  icones?: Partial<Record<EstadoDaAcao, ReactNode>>;
  /** Onde as faces se alinham na caixa (dentro de botão: "centro"). */
  alinhar?: "inicio" | "centro" | "fim";
  className?: string;
  style?: CSSProperties;
}) {
  const mov = useMovimento();
  const faces: EstadoDaAcao[] = rotulos.erro ? ["parado", "fazendo", "feito", "erro"] : ["parado", "fazendo", "feito"];
  const visivel = mov.r.rotulo.animate;
  const escondido = estado === "parado" ? mov.r.rotulo.exit : mov.r.rotulo.initial;

  const iconePadrao = (face: EstadoDaAcao): ReactNode => {
    if (icones && face in icones) return icones[face];
    if (face === "fazendo")
      /* Mora montado (a face guarda a largura da caixa), mas só gira quando
         está na tela: escondido, um girador sem fim é trabalho à toa. */
      return (
        <span
          className="mov-girador"
          aria-hidden
          style={estado === "fazendo" ? undefined : { animationPlayState: "paused" }}
        />
      );
    if (face === "feito") return <MarcaDeOk tamanho={13} vez={estado === "feito" ? 1 : 0} />;
    return null;
  };

  return (
    <span
      className={className}
      data-estado={estado}
      /* Só `aria-live`, sem `role="status"`: com o papel, o texto saía do nome
         acessível do botão em volta (o botão ficava sem nome). A face ativa é
         o nome; a troca de face é anunciada. */
      aria-live="polite"
      style={{
        display: "inline-grid",
        alignItems: "center",
        justifyItems: alinhar === "centro" ? "center" : alinhar === "fim" ? "end" : "start",
        ...style,
      }}
    >
      {faces.map((face) => {
        const ativa = face === estado;
        return (
          <m.span
            key={face}
            aria-hidden={!ativa}
            className={`mov-face mov-face-${face}`}
            initial={false}
            animate={ativa ? visivel : escondido}
            style={{
              gridArea: "1 / 1",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
              pointerEvents: ativa ? undefined : "none",
            }}
          >
            {iconePadrao(face)}
            {rotulos[face]}
          </m.span>
        );
      })}
    </span>
  );
}
